import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  isDevMode,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonButton, IonButtons, IonHeader, IonIcon, IonModal, IonToolbar } from '@ionic/angular/standalone';
import { ThemeStore } from '@libs/core/lib/utils/theme/theme.store';
import { decode } from 'blurhash';
import { addIcons } from 'ionicons';
import { close } from 'ionicons/icons';
import { Subscription } from 'rxjs';

@Component({
  selector: 'mtjr-img',
  imports: [IonModal, IonButton, IonHeader, IonButtons, IonIcon, IonToolbar, NgClass],
  template: `
    <div class="relative flex items-center justify-center">
      <!-- Blurhash canvas placeholder -->
      <canvas #blurhashCanvas [class]="'absolute inset-0 z-0 ' + imgClass()"></canvas>

      <!-- Main image -->
      @if (proxiedSrc()) {
        <img
          [src]="proxiedSrc()"
          [alt]="alt()"
          [class]="'z-1 cursor-pointer hover:opacity-75 transition-opacity duration-700 ' + imgClass()"
          [class.opacity-0]="!imageLoaded() && blurHash()"
          [class.opacity-100]="imageLoaded() || !blurHash()"
          (click)="$event.preventDefault(); $event.stopPropagation(); void (disabled() ? null : isOpen.set(true))"
          (load)="onImageLoad()"
          (error)="onImageError()"
          [ngClass]="{
            'z-10': imageLoaded(),
            sticky: imageLoaded(),
          }"
        />
      }
    </div>

    <ion-modal [isOpen]="isOpen()" (willDismiss)="isOpen.set(false)" cssClass="rounded-dialog">
      <ng-template>
        <ion-header [translucent]="true" class="relative z-10">
          <ion-toolbar #transToolbar class="absolute" style="--background: transparent; --border-color: transparent">
            <ion-buttons slot="start">
              <ion-button (click)="isOpen.set(false)">
                <ion-icon name="close" slot="icon-only"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <img [src]="proxiedSrcFull()" [alt]="alt()" class="h-full w-full object-contain" />
      </ng-template>
    </ion-modal>
  `,
})
export class VxImageComponent implements OnDestroy {
  imgClass = input('');
  src = input.required<string | undefined | null>();
  proxy = input<string | undefined>('https://dev-img.mtjr.ly/unsafe/resize:auto:200:200/sm:1/q:85/plain/');
  proxyFull = input<string | undefined>('https://dev-img.mtjr.ly/unsafe/resize:auto:1280:720/sm:1/q:85/plain/');
  proxiedSrc = computed(() => {
    const proxy = this.proxy();
    const src = this.src();
    return proxy && src ? proxy + src : src || '';
  });
  proxiedSrcFull = computed(() => {
    const proxyFull = this.proxyFull();
    const src = this.src();
    return proxyFull && src ? proxyFull + src : src || '';
  });
  alt = input('');
  blurHash = input<string | undefined | null>();
  isOpen = signal(false);
  disabled = input(false);
  router = inject(Router);
  route = inject(ActivatedRoute);
  routeSub?: Subscription;
  isDevMode = isDevMode();

  // Blurhash and image loading state
  imageLoaded = signal(false);
  blurhashCanvas = viewChild<ElementRef<HTMLCanvasElement>>('blurhashCanvas');

  constructor() {
    addIcons({ close });

    // todo: fixme: this navigation should be done using signals
    // effect(() => {
    //   this.router.navigate([], {
    //     relativeTo: this.route,
    //     queryParams: { img: this.isOpen() ? this.src() : null },
    //     queryParamsHandling: 'merge',
    //   });
    // });

    effect(() => {
      this.routeSub = this.route.queryParams.subscribe((params) => {
        this.isOpen.set(params['img'] === this.src());
      });
    });

    // Effect to render blurhash when blurHash or src changes
    effect(() => {
      const blurHash = this.blurHash();
      const canvas = this.blurhashCanvas()?.nativeElement;

      if (blurHash && canvas) {
        this.renderBlurhash(blurHash, canvas);
      }

      // Reset image loaded state when src changes
      this.imageLoaded.set(false);
    });
  }

  onImageLoad() {
    this.imageLoaded.set(true);
  }

  onImageError() {
    this.imageLoaded.set(true); // Hide blurhash even on error
  }

  private renderBlurhash(blurhash: string, canvas: HTMLCanvasElement) {
    try {
      const width = 32;
      const height = 32;

      canvas.width = width;
      canvas.height = height;

      const pixels = decode(blurhash, width, height);
      const ctx = canvas.getContext('2d');

      if (ctx) {
        const imageData = ctx.createImageData(width, height);
        imageData.data.set(pixels);
        ctx.putImageData(imageData, 0, 0);
      }
    } catch (error) {
      console.warn('Failed to render blurhash:', error);
    }
  }

  ngOnDestroy() {
    this.isOpen.set(false);
    this.routeSub?.unsubscribe();
  }
}

@Component({
  standalone: true,
  selector: 'mtjr-dynamic-img',
  template: `
    <img [src]="effectiveSrc()" [alt]="effectiveSrc()" [class]="class()" />
  `,
})
export class VxDynamicImageComponent {
  class = input('');
  src = input.required<string | undefined>();
  darkSrc = input.required<string | undefined>();
  alt = input('');
  themeStore = inject(ThemeStore);

  effectiveSrc = computed(() => {
    return this.themeStore.usingDarkTheme() ? this.darkSrc() : this.src();
  });
}
